import React, { ReactElement } from 'react';

import { SingleTemplateProps } from './types';
import { MemoSingleTemplatePresentational } from './presentational';
import {
  useInspectionTemplate,
  useInspectionTemplateInitializer,
} from 'store/inspection';
import { SelectTemplatePrompt } from './SelectTemplatePrompt';
import { TemplateListErrorText } from 'views/template/list/types';
import { Spinner } from 'components/core';
import { MemoSingleTemplateError } from '../SingleError';
import {
  UrlParamName,
  useAppUrlParams,
} from 'hooks/navigation/useAppUrlParams';

function SingleTemplate(props: SingleTemplateProps): ReactElement {
  const {
    error,
    template,
    canEditTemplate,
    editTemplate,
    canRemoveTemplate,
    removeTemplate,
    archiveTemplate,
    restoreTemplate,
    startInspection,
    canStartInspection,
  } = props;
  const templateIdFromUrl = useAppUrlParams(
    UrlParamName.INSPECTION_TEMPLATE_ID
  );
  const templateId = template?._id || templateIdFromUrl;

  useInspectionTemplateInitializer(templateId);
  const { inspectionTemplateDocuments, loading: loadingDocuments } = useInspectionTemplate();

  const visibleChecklistItems = React.useMemo(
    () =>
      template ? template.checklist.filter((item) => !item.deleted) : [],
    [template]
  );

  const visibleSections = React.useMemo(
    () => (template ? template.sections.filter((section) => !section.deleted) : []),
    [template]
  );

  switch (error?.templateErrorType) {
    case TemplateListErrorText.noTemplates:
      return <SelectTemplatePrompt />;
    case TemplateListErrorText.noFilteredTemplates:
      return <MemoSingleTemplateError error={error} />;
    default:
      break;
  }

  return template ? (
    <MemoSingleTemplatePresentational
      template={template}
      generalNormDocuments={inspectionTemplateDocuments.templateDocuments}
      loadingDocuments={loadingDocuments}
      canEditTemplate={canEditTemplate}
      editTemplate={editTemplate}
      canRemoveTemplate={canRemoveTemplate}
      removeTemplate={removeTemplate}
      archiveTemplate={archiveTemplate}
      restoreTemplate={restoreTemplate}
      checklist={visibleChecklistItems}
      sections={visibleSections}
      startInspection={startInspection}
      canStartInspection={canStartInspection}
    />
  ) : (
    <Spinner position='static' reason='MemoSingleTemplate !template' />
  );
}

export const MemoSingleTemplate = React.memo(SingleTemplate);
